import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { DatePickerField } from './ui/date-picker-field';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Calendar, Plus, Trash2, Save, RotateCcw, Loader2, Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  test: 'Test',
  prod: 'Full Release',
  maintenance: 'Maintenance',
};
const TYPE_COLORS = {
  test: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  prod: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  maintenance: 'bg-amber-100 text-amber-800 border-amber-200',
};

const blankRow = () => ({
  id: '',
  quarter: '',
  release_type: 'test',
  date: '',
  label: '',
});

export default function PublicCloudReleasesEditor() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/public-cloud-releases`, { withCredentials: true });
      setEvents(res.data?.events || []);
    } catch {
      toast.error('Could not load releases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleAdd = () => setEvents((prev) => [...prev, blankRow()]);
  const handleRemove = (idx) => setEvents((prev) => prev.filter((_, i) => i !== idx));
  const handleChange = (idx, field, value) => {
    setEvents((prev) => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const handleSave = async () => {
    // Validate
    for (let i = 0; i < events.length; i++) {
      const e = events[i];
      if (!e.quarter || !e.release_type || !e.date) {
        toast.error(`Row ${i + 1}: quarter, type, and date are required`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/settings/public-cloud-releases`,
        { events: events.map((e, i) => ({
            ...e,
            id: e.id || `${e.quarter}-${e.release_type}-${e.date}-${i}`.replace(/\s+/g, '-').toLowerCase(),
          })) },
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Saved');
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Quick template — seeds 12 events for 4 quarters of a chosen year.
  const handleSeedYear = () => {
    const yyStr = window.prompt('Seed year (e.g., 26 for 2026)?', '26');
    if (!yyStr) return;
    const yy = String(yyStr).padStart(2, '0');
    const yearNum = 2000 + parseInt(yy, 10);
    if (Number.isNaN(yearNum)) return;
    const seeded = [];
    [1, 2, 3, 4].forEach((q) => {
      const monthBase = (q - 1) * 3;
      // Test: mid-month 1 of quarter; Prod: mid-month 2; Maintenance: end of month 3.
      const dates = [
        new Date(yearNum, monthBase, 15),
        new Date(yearNum, monthBase + 1, 15),
        new Date(yearNum, monthBase + 2, 28),
      ];
      ['test', 'prod', 'maintenance'].forEach((rt, idx) => {
        const d = dates[idx];
        const iso = d.toISOString().slice(0, 10);
        seeded.push({
          id: `q${q}-${yy}.${q}-${rt}`,
          quarter: `Q${q} ${yy}.${q}`,
          release_type: rt,
          date: iso,
          label: `${yy}.${q} ${TYPE_LABELS[rt]}`,
        });
      });
    });
    setEvents(seeded);
    toast.message('Seeded 12 events — review and Save.');
  };

  const filtered = useMemo(() => {
    if (!filter.trim()) return events;
    const q = filter.toLowerCase();
    return events.filter((e) =>
      (e.quarter || '').toLowerCase().includes(q)
      || (e.label || '').toLowerCase().includes(q)
      || (e.release_type || '').toLowerCase().includes(q)
      || (e.date || '').includes(q)
    );
  }, [events, filter]);

  return (
    <Card data-testid="public-cloud-releases-card">
      <CardHeader>
        <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Public Cloud Release Calendar
        </CardTitle>
        <CardDescription>
          Maintain Test, Full Release, and Maintenance windows so the Planner Gantt can render them as
          visibility markers. Typical cadence: 4 Test + 4 Prod + 4 Maintenance per year, grouped by
          quarter (e.g., "Q1 26.1 Test", "Q1 26.1 Prod", "Q1 26.1 Maintenance").
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Filter by quarter, label, type, or date…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs"
            data-testid="releases-filter"
          />
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={handleSeedYear}
            disabled={saving}
            data-testid="releases-seed-year-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Seed year
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={saving}
            data-testid="releases-add-row-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add row
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="releases-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-md flex flex-col items-center gap-2">
            <Info className="w-4 h-4" />
            No release events configured. Use "Seed year" to bootstrap 12 events or "Add row" to start fresh.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="releases-table">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Quarter</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Label</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((evt, idx) => {
                  const realIdx = events.indexOf(evt);
                  return (
                    <tr key={`${evt.id || 'new'}-${idx}`} data-testid={`releases-row-${realIdx}`}>
                      <td className="px-3 py-1.5">
                        <Input
                          value={evt.quarter}
                          onChange={(e) => handleChange(realIdx, 'quarter', e.target.value)}
                          placeholder="Q1 26.1"
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Select
                          value={evt.release_type}
                          onValueChange={(v) => handleChange(realIdx, 'release_type', v)}
                        >
                          <SelectTrigger className="h-8 text-sm w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(TYPE_LABELS).map(([k, label]) => (
                              <SelectItem key={k} value={k}>
                                <Badge variant="outline" className={`${TYPE_COLORS[k]} text-[11px]`}>{label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-1.5">
                        <DatePickerField
                          value={evt.date || ''}
                          onChange={(iso) => handleChange(realIdx, 'date', iso)}
                          testId={`public-cloud-release-date-${realIdx}`}
                          placeholder="Pick date"
                          widthClass="w-[160px]"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          value={evt.label}
                          onChange={(e) => handleChange(realIdx, 'label', e.target.value)}
                          placeholder="26.1 Release Test"
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                          onClick={() => handleRemove(realIdx)}
                          data-testid={`releases-remove-${realIdx}`}
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {events.length > 0 && (
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <Info className="w-3 h-3" />
            <span>
              These events overlay on the Planner Gantt as vertical markers so PMs can avoid scheduling go-lives inside maintenance windows or land just before a release.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
